import { useApolloClient } from '@apollo/client';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, TouchableOpacity } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import Highlighter from '@components/HighlightWords';
import { ContactType } from '@components/Invite/Contact';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { RestyleProps } from '@components/shared/Icon/RestyleIcon';
import { useFriendRequestMutation } from '@graphql/generated';
import useMe from '@hooks/useMe';
import theme from '@themes/theme';

interface ContactProps {
  contact: ContactType;
  filterVal?: string;
  onPress: (item: ContactType) => void;
  onPressAvatar?: (item: ContactType) => void;
  isEditing?: boolean;
  isSelected?: boolean;
  isInviting?: boolean;
  shouldDisplayPhoneContactLabel?: boolean;
  disabled?: boolean;
  showMessageIcon?: boolean;
  onMessagePress?: (item: ContactType) => void;
  style?: RestyleProps;
  isTaskTagUser?: boolean;
  toggleFriendRequestSuccess?: (friendRequest: boolean) => void;
  isContactGroup?: boolean;
  isChat?: boolean;
  alreadyInvited?: boolean;
}

const ContactV3: React.FC<ContactProps> = ({
  contact,
  onPress,
  filterVal = '',
  isEditing,
  isSelected,
  isInviting,
  shouldDisplayPhoneContactLabel = true,
  disabled = false,
  showMessageIcon = false,
  onMessagePress,
  isContactGroup = false,
  isChat = false,
  style = { paddingHorizontal: 'm' },
  onPressAvatar,
  isTaskTagUser = false,
  alreadyInvited = false,
}) => {
  const { t } = useTranslation('models');
  const { t: f } = useTranslation('format');
  const { inviteViaPhone } = useMe();

  const {
    id,
    avatar,
    phoneNumber,
    isPhoneContact,
    displayName,
    name,
    roleLabel,

    friendRequestPending,
    avatarSize = 'medium',
  } = contact;

  const [statusBtn, setStatusBtn] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [friendRequest] = useFriendRequestMutation();
  const [friendRequestSuccess, setFriendRequestSuccess] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const toast = useToast();
  const client = useApolloClient();
  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };
  useEffect(() => {
    if (friendRequestPending) setStatusBtn('Pending');
    else setStatusBtn('Add');
  }, [friendRequestPending]);

  const radioButtonCom =
    disabled || alreadyInvited ? (
      <Icon name='StateYes' color='grey04'></Icon>
    ) : (
      <RadioButton
        onPress={() => onPress?.(contact)}
        isSelected={!!isSelected}
        iconVariant='m'
        disabled={disabled}
      />
    );
  return (
    <Box
      py='xs'
      flexDirection='row'
      alignItems='center'
      backgroundColor={isHovered ? 'grey01' : 'white'}
      justifyContent='space-between'
      {...style}
      {...(Platform.OS === 'web'
        ? {
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          }
        : {})}>
      <Avatar
        avatarType={isChat ? 'chat' : 'user'}
        id={id}
        avatar={avatar}
        size={avatarSize}
        label={displayName ?? name}
        isMultiple={isContactGroup}
        onPress={
          onPressAvatar
            ? () => {
                onPressAvatar(contact);
              }
            : undefined
        }
        disableAvatarPress={showMessageIcon || isContactGroup}
      />

      <Box marginLeft='xs' flex={1}>
        <TouchableOpacity
          testID={contact.id}
          onPress={() => {
            if (isTaskTagUser) return;
            onPressAvatar?.(contact);
          }}
          disabled={
            disabled ||
            alreadyInvited ||
            (statusBtn === 'Pending' && isTaskTagUser)
          }>
          <Text
            accessibilityLabel='Member'
            variant='body1'
            numberOfLines={1}
            color={(displayName ?? name) ? 'textPrimary' : 'grey04'}>
            {(displayName ?? name) ? (
              <Highlighter
                highlightStyle={
                  Platform.OS === 'web'
                    ? {
                        backgroundColor: theme.colors.yellowBright,
                      }
                    : {
                        color: theme.colors.greenSecondary,
                        backgroundColor: theme.colors.background,
                      }
                }
                autoEscape
                searchWords={filterVal?.split(' ') || ''}
                textToHighlight={displayName ?? name}
              />
            ) : (
              'No name'
            )}
          </Text>

          <Box>
            {contact?.users?.length > 0 && (
              <TouchableOpacity onPress={toggleVisibility}>
                <Box>
                  <Text
                    marginTop='xxs'
                    variant='bodySecondary'
                    color='textSecondary'>
                    {'Contact Group (' + contact.users.length + ')'}
                    <Icon
                      name={isVisible ? 'ChevronDown' : 'ChevronRight'}
                      style={{ width: 40, height: 40, marginTop: 4 }}
                      color='textSecondary'
                    />
                  </Text>
                </Box>
              </TouchableOpacity>
            )}
          </Box>
          {roleLabel && (
            <Text marginTop='xxs' variant='bodySecondary' color='grey04'>
              {t(roleLabel)}
            </Text>
          )}
          {alreadyInvited && (
            <Text
              variant='bodySecondary'
              color='greenSecondary'
              numberOfLines={1}
              ellipsizeMode='tail'>
              {t('chat.alreadyRequested')}
            </Text>
          )}
          {!alreadyInvited && !disabled && contact.email && (
            <Text
              variant='bodySecondary'
              color='grey04'
              numberOfLines={1}
              ellipsizeMode='tail'>
              <Highlighter
                highlightStyle={
                  Platform.OS === 'web'
                    ? {
                        backgroundColor: theme.colors.yellowBright,
                      }
                    : {
                        color: theme.colors.greenSecondary,
                        backgroundColor: theme.colors.background,
                      }
                }
                autoEscape
                searchWords={filterVal?.split(' ') || ''}
                textToHighlight={contact.email}
              />
            </Text>
          )}
          {!alreadyInvited && disabled && (
            <Text
              variant='bodySecondary'
              color='greenSecondary'
              numberOfLines={1}
              ellipsizeMode='tail'>
              {t('models:chat.alreadyInConversation')}
            </Text>
          )}
          {!roleLabel && isPhoneContact && shouldDisplayPhoneContactLabel && (
            <Text marginTop='xxs' variant='bodySecondary' color='textSecondary'>
              {t('models:chat.fromPhoneContact')}
            </Text>
          )}
          {isInviting && (
            <Text variant='bodySecondary' color='textSecondary' marginTop='xxs'>
              {f('phoneNumber', {
                val: phoneNumber,
              })}
            </Text>
          )}
        </TouchableOpacity>
      </Box>

      {isPhoneContact && phoneNumber && shouldDisplayPhoneContactLabel && (
        <TouchableOpacity onPress={() => inviteViaPhone(phoneNumber)}>
          <Box
            borderColor='textPrimary'
            borderWidth={1}
            borderRadius='xs'
            paddingHorizontal='l'
            paddingVertical='xxs'>
            <Text>Invite</Text>
          </Box>
        </TouchableOpacity>
      )}
      {showMessageIcon && (
        <Icon
          name='MessageCircle'
          variant='l'
          testId='message-icon'
          marginRight='m'
          onPress={() => onMessagePress?.(contact)}
        />
      )}
      {isEditing && (
        <Box justifyContent='center' marginRight='xs'>
          {isTaskTagUser ? (
            <Button
              padding='none'
              marginLeft='xs'
              isSmall={true}
              variant='edit'
              borderColor={'#000' as any}
              borderRadius='xs'
              borderWidth={1}
              disabled={
                alreadyInvited || friendRequestPending || friendRequestSuccess
              }
              height={32}
              width={72}
              backgroundColor={
                friendRequestPending || friendRequestSuccess ? 'black' : 'white'
              }
              onPress={() => {
                setFriendRequestSuccess(true);
                friendRequest({
                  onCompleted: () => {
                    const userRef = client.cache.identify({
                      __typename: 'User',
                      id: contact.id,
                    });
                    if (!userRef) return;

                    client.cache.modify({
                      id: userRef,
                      fields: {
                        friendRequestPending() {
                          return true;
                        },
                      },
                    });
                    toast.hideAll();
                    toast.show(t('models:chat.connectionRequestSend'), {
                      duration: 3000,
                    });
                    setTimeout(() => {
                      setFriendRequestSuccess(false);
                    }, 500);
                  },
                  onError: () => {
                    toast.hideAll();
                    toast.show('Failed, please try again later.', {
                      duration: 3000,
                    });
                    setTimeout(() => {
                      setFriendRequestSuccess(false);
                    }, 500);
                  },
                  variables: {
                    attributes: {
                      userIds: [contact.id],
                    },
                  },
                });
              }}
              accessibilityLabel={t('shared:Join')}>
              <Text
                variant='webBodyHintSubline'
                color={
                  friendRequestPending || friendRequestSuccess
                    ? 'white'
                    : 'black'
                }>
                {friendRequestPending ? 'Pending' : 'Add'}
              </Text>
            </Button>
          ) : (
            radioButtonCom
          )}
        </Box>
      )}
    </Box>
  );
};

export default memo(ContactV3);
